import {
  type Updater,
  queryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import * as TypeCompiler from 'typebox/compile';
import { ReadonlyArray } from 'schema/schema';
import { CountryWithUserCounts, OfficeWithUserCounts } from 'schema/shared';
import { http, httpChecked, queryClient } from 'util/fetch';
import type { Template } from './Templates';

const countryChecker = TypeCompiler.Compile(CountryWithUserCounts);
const countriesChecker = TypeCompiler.Compile(
  ReadonlyArray(CountryWithUserCounts),
);
const officeChecker = TypeCompiler.Compile(OfficeWithUserCounts);

const queries = {
  all: queryOptions({
    queryKey: ['countries', 'all'] as const,
    queryFn: ({ signal }) =>
      httpChecked.get('/api/v1/countries/', countriesChecker, { signal }),
  }),
  allowed: queryOptions({
    queryKey: ['countries', 'allowed'] as const,
    queryFn: ({ signal }) =>
      httpChecked.get(
        '/api/v1/countries/?only_allowed=true',
        countriesChecker,
        { signal },
      ),
  }),
};

export async function prefetchAllowedCountries() {
  await queryClient.prefetchQuery({ ...queries.allowed, staleTime: 60_000 });
}

export async function prefetchAllCountries() {
  await queryClient.prefetchQuery({ ...queries.all, staleTime: 60_000 });
}

export function useAllowedCountries() {
  const { data, isLoading } = useQuery({
    ...queries.allowed,
    staleTime: 60_000,
  });

  return { data, isLoading };
}

export function useAllCountries() {
  const { data } = useQuery({ ...queries.all, staleTime: 60_000 });

  return data;
}

async function invalidateQueries() {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queries.all.queryKey,
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: queries.allowed.queryKey,
      refetchType: 'all',
    }),
  ]);
}

interface UpdateTemplateVariables {
  country_id: string;
  template: Pick<Template, '_id' | 'template_type'> | null;
}

export function useUpdateTemplate() {
  const { mutate } = useMutation({
    mutationFn: async ({ country_id, template }: UpdateTemplateVariables) => {
      const json = {
        country: {
          cv_template_id: template?._id ?? null,
          cv_template_type: template?.template_type ?? null,
        },
      };

      const url = `/api/v1/countries/${country_id}`;
      return httpChecked.put(url, countryChecker, { body: json });
    },
    onSuccess: (updated_country, _vars, _onMutateResult, { client }) => {
      let invalidate = false;

      const countryUpdater: Updater<
        readonly CountryWithUserCounts[] | undefined,
        readonly CountryWithUserCounts[] | undefined
      > = (countries) => {
        if (!countries) {
          return undefined;
        }
        const index = countries.findIndex(
          (country) => country._id === updated_country._id,
        );

        if (index === -1) {
          invalidate = true;
          return undefined;
        }

        return [
          ...countries.slice(0, index),
          updated_country,
          ...countries.slice(index + 1),
        ];
      };

      client.setQueryData(queries.all.queryKey, countryUpdater);
      client.setQueryData(queries.allowed.queryKey, countryUpdater);

      if (invalidate) {
        return invalidateQueries();
      }
    },
    onError: invalidateQueries,
  });

  return mutate;
}

const createOfficeUpdater = (
  updated_office: OfficeWithUserCounts,
  shouldInvalidate: () => void,
): Updater<
  readonly CountryWithUserCounts[] | undefined,
  readonly CountryWithUserCounts[] | undefined
> => {
  return (countries) => {
    if (!countries) {
      shouldInvalidate();
      return undefined;
    }
    const updatedCountry = countries.find(
      (country) => country._id === updated_office.country_id,
    );

    if (updatedCountry == null) {
      shouldInvalidate();
      return undefined;
    }

    const countryIndex = countries.indexOf(updatedCountry);

    const officeIndex = updatedCountry.offices.findIndex(
      (office) => office._id === updated_office._id,
    );

    if (officeIndex === -1) {
      shouldInvalidate();
      return undefined;
    }

    return [
      ...countries.slice(0, countryIndex),
      {
        ...updatedCountry,
        offices: [
          ...updatedCountry.offices.slice(0, officeIndex),
          updated_office,
          ...updatedCountry.offices.slice(officeIndex + 1),
        ],
      },
      ...countries.slice(countryIndex + 1),
    ];
  };
};

interface UpdateOfficeTemplateVariables {
  country_id: string;
  office_id: string;
  template: Pick<Template, '_id' | 'template_type'> | null;
}

export function useUpdateOfficeTemplate() {
  const { mutate } = useMutation({
    mutationFn: async ({
      country_id,
      office_id,
      template,
    }: UpdateOfficeTemplateVariables) => {
      const json = {
        office: {
          cv_template_id: template?._id ?? null,
          cv_template_type: template?.template_type ?? null,
        },
      };
      const url = `/api/v1/countries/${country_id}/offices/${office_id}`;
      return httpChecked.put(url, officeChecker, { body: json });
    },
    onSuccess: (updated_office, _vars, _onMutateResult, { client }) => {
      let invalidate = false;

      const officeUpdater = createOfficeUpdater(updated_office, () => {
        invalidate = true;
      });

      client.setQueryData(queries.all.queryKey, officeUpdater);
      client.setQueryData(queries.allowed.queryKey, officeUpdater);

      if (invalidate) {
        return invalidateQueries();
      }
    },
    onError: invalidateQueries,
  });

  return mutate;
}

interface UpdateOfficeVariables {
  country_id: string;
  office_id: string;
  updated_office: { name?: string; country_id?: string };
}

export function useUpdateOffice() {
  const { mutate } = useMutation({
    mutationFn: async ({
      country_id,
      office_id,
      updated_office,
    }: UpdateOfficeVariables) => {
      const json: { office: UpdateOfficeVariables['updated_office'] } = {
        office: {},
      };

      const name = updated_office.name;
      if (name) {
        json.office.name = name;
      }
      const new_country_id = updated_office.country_id;
      if (new_country_id && new_country_id !== country_id) {
        json.office.country_id = new_country_id;
      }

      const url = `/api/v1/countries/${country_id}/offices/${office_id}`;
      return httpChecked.put(url, officeChecker, { body: json });
    },
    onSuccess: (
      updated_office,
      { country_id },
      _onMutateResult,
      { client },
    ) => {
      // if office changed country, just invalidate everything
      if (updated_office.country_id !== country_id) {
        return invalidateQueries();
      }

      let invalidate = false;

      const officeUpdater = createOfficeUpdater(updated_office, () => {
        invalidate = true;
      });

      client.setQueryData(queries.all.queryKey, officeUpdater);
      client.setQueryData(queries.allowed.queryKey, officeUpdater);

      if (invalidate) {
        return invalidateQueries();
      }
    },
    onError: invalidateQueries,
  });

  return mutate;
}

interface CreateOfficeVariables {
  country_id: string;
  name: string;
}

export function useCreateOffice() {
  const { mutate } = useMutation({
    mutationFn: async ({ country_id, name }: CreateOfficeVariables) => {
      const json = { name };
      const url = `/api/v1/countries/${country_id}/offices`;
      return httpChecked.post(url, officeChecker, { body: json });
    },
    onSuccess: (new_office, _vars, _onMutateResult, { client }) => {
      let invalidate = false;

      const officeUpdater: Updater<
        readonly CountryWithUserCounts[] | undefined,
        readonly CountryWithUserCounts[] | undefined
      > = (countries) => {
        if (!countries) {
          invalidate = true;
          return undefined;
        }
        const updatedCountry = countries.find(
          (country) => country._id === new_office.country_id,
        );

        if (updatedCountry == null) {
          invalidate = true;
          return undefined;
        }

        const countryIndex = countries.indexOf(updatedCountry);

        return [
          ...countries.slice(0, countryIndex),
          {
            ...updatedCountry,
            offices: [...updatedCountry.offices, new_office],
          },
          ...countries.slice(countryIndex + 1),
        ];
      };

      client.setQueryData(queries.all.queryKey, officeUpdater);
      client.setQueryData(queries.allowed.queryKey, officeUpdater);

      if (invalidate) {
        return invalidateQueries();
      }
    },
    onError: invalidateQueries,
  });

  return mutate;
}

interface DeleteOfficeVariables {
  country_id: string;
  office_id: string;
}

export function useDeleteOffice() {
  const { mutate } = useMutation({
    mutationFn: async ({ country_id, office_id }: DeleteOfficeVariables) => {
      const url = `/api/v1/countries/${country_id}/offices/${office_id}`;
      return http.delete(url);
    },
    onSuccess: (_data, deleted_office, _onMutateResult, { client }) => {
      let invalidate = false;

      const officeUpdater: Updater<
        readonly CountryWithUserCounts[] | undefined,
        readonly CountryWithUserCounts[] | undefined
      > = (countries) => {
        if (!countries) {
          invalidate = true;
          return undefined;
        }
        const updatedCountry = countries.find(
          (country) => country._id === deleted_office.country_id,
        );

        if (updatedCountry == null) {
          invalidate = true;
          return undefined;
        }

        const countryIndex = countries.indexOf(updatedCountry);

        return [
          ...countries.slice(0, countryIndex),
          {
            ...updatedCountry,
            offices: updatedCountry.offices.filter(
              (office) => office._id !== deleted_office.office_id,
            ),
          },
          ...countries.slice(countryIndex + 1),
        ];
      };

      client.setQueryData(queries.all.queryKey, officeUpdater);
      client.setQueryData(queries.allowed.queryKey, officeUpdater);

      if (invalidate) {
        return invalidateQueries();
      }
    },
    onError: invalidateQueries,
  });

  return mutate;
}

export interface CreateCountryVariables {
  code: string;
  native_language_code: string;
  override_ui_language_code: string | null;
}

export function useCreateCountry() {
  const { mutate } = useMutation({
    mutationFn: async (new_country: CreateCountryVariables) => {
      const json = { country: new_country };
      const url = `/api/v1/countries`;
      return httpChecked.post(url, countryChecker, { body: json });
    },
    onSettled: invalidateQueries,
  });

  return mutate;
}

interface UpdateCountryVariables extends CreateCountryVariables {
  country_id: string;
}

export function useUpdateCountry() {
  const { mutate } = useMutation({
    mutationFn: async ({
      country_id,
      ...updated_country
    }: UpdateCountryVariables) => {
      const json = { country: updated_country };
      const url = `/api/v1/countries/${country_id}`;
      return httpChecked.put(url, countryChecker, { body: json });
    },
    onSettled: invalidateQueries,
  });

  return mutate;
}
